import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '../../../components/ui/Badge';
import {
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  CHILD_TYPE_LABELS,
  NODE_TYPE_LABELS,
  type PmsChildRow,
  type PmsNodeType,
} from './buildPmsHierarchyTree';
import { PMS_HIERARCHY_PATH } from './pmsHierarchySession';

type ChildTab = 'all' | 'equipment' | 'instance' | 'job' | 'other';

const TYPE_BADGE: Record<PmsNodeType, string> = {
  vessel: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200',
  department: 'bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-200',
  system: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-200',
  component: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200',
  subcomponent: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  equipment: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-200',
  instance: 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-950/50 dark:text-fuchsia-200',
  job: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-200',
};

const DUE_STATUS_STYLE: Record<string, string> = {
  OVERDUE: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
  DUE: 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300',
  WARNING: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
  PLANNED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  CONDITION: 'bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
  UNKNOWN: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const dueStatusLabel = (status?: string | null) => {
  const s = (status ?? '').toUpperCase();
  if (!s || s === '-') return null;
  if (s === 'WARNING') return 'Due soon';
  if (s === 'PLANNED') return 'OK';
  if (s === 'CONDITION') return 'Observation';
  return s.charAt(0) + s.slice(1).toLowerCase();
};

const DueCell: React.FC<{ nextDue?: string | null; status?: string | null }> = ({ nextDue, status }) => {
  const label = dueStatusLabel(status);
  const styleKey = (status ?? 'UNKNOWN').toUpperCase();
  return (
    <span className="inline-flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-sm">{Boolean(nextDue) ? nextDue : ''}</span>
      {label ? (
        <span
          className={cn(
            'inline-flex w-fit rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            DUE_STATUS_STYLE[styleKey] ?? DUE_STATUS_STYLE.UNKNOWN,
          )}
        >
          {label}
        </span>
      ) : null}
    </span>
  );
};

export type HierarchyCreateAction = {
  type: Exclude<PmsNodeType, 'vessel'>;
  href: string;
  label: string;
  hint: string;
  primary?: boolean;
};

function identityValue(row: PmsChildRow) {
  if (row.type === 'instance') return row.fields?.serialNumber ?? '';
  if (row.type === 'job') return row.fields?.jobType ?? '';
  if (row.type === 'equipment') return row.code || '';
  return row.code || '';
}

function statusValue(row: PmsChildRow) {
  if (row.type === 'instance') return row.fields?.status ?? '';
  if (row.type === 'job') return row.fields?.criticality ?? '';
  return '';
}

function whereValue(row: PmsChildRow) {
  if (row.type === 'instance') return row.fields?.installedLocation ?? '';
  if (row.type === 'job') return row.fields?.department ?? '';
  return '';
}

function scheduleValue(row: PmsChildRow) {
  if (row.type !== 'job') return '';
  const mode = row.fields?.scheduleMode;
  const freq = row.fields?.frequency;
  if (mode && mode !== '-' && freq && freq !== '-') return `${mode} · ${freq}`;
  return mode ?? freq ?? '';
}

function sortValue(row: PmsChildRow, key: string): string {
  switch (key) {
    case 'type':
      return NODE_TYPE_LABELS[row.type].toLowerCase();
    case 'name':
      return (row.name || row.fields?.title || '').toLowerCase();
    case 'nextDue':
      return String(row.fields?.nextDue ?? '');
    case 'identity':
      return identityValue(row).toLowerCase();
    case 'status':
      return statusValue(row).toLowerCase();
    case 'where':
      return whereValue(row).toLowerCase();
    case 'schedule':
      return scheduleValue(row).toLowerCase();
    default:
      return '';
  }
}

function tabForType(type: PmsNodeType): ChildTab {
  if (type === 'equipment' || type === 'instance' || type === 'job') return type;
  return 'other';
}

type Props = {
  title: string;
  description?: string;
  selectionType: PmsNodeType;
  childRows: PmsChildRow[];
  createActions: HierarchyCreateAction[];
  openSelectedHref: string | null;
  editSelectedHref: string | null;
  isLoading?: boolean;
  onPersistUi: () => void;
  onDelete: (row: PmsChildRow) => void;
  canDeleteType: (type: PmsNodeType) => boolean;
};

export const HierarchyChildrenInbox: React.FC<Props> = ({
  title,
  description,
  selectionType,
  childRows,
  createActions,
  openSelectedHref,
  editSelectedHref,
  isLoading,
  onPersistUi,
  onDelete,
  canDeleteType,
}) => {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [tab, setTab] = useState<ChildTab>('all');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setTab('all');
    setSearchInput('');
    setSearch('');
    setPage(1);
  }, [title, selectionType]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort({ key: 'name', direction: 'asc' });

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const counts = useMemo(() => {
    let equipment = 0;
    let instance = 0;
    let job = 0;
    let other = 0;
    for (const row of childRows) {
      const t = tabForType(row.type);
      if (t === 'equipment') equipment += 1;
      else if (t === 'instance') instance += 1;
      else if (t === 'job') job += 1;
      else other += 1;
    }
    return { equipment, instance, job, other, all: childRows.length };
  }, [childRows]);

  const viewTabs = useMemo(() => {
    const tabs: Array<{
      id: ChildTab;
      label: string;
      icon: React.ReactNode;
      count: number;
      emptyTitle: string;
      emptyHint: string;
    }> = [
      {
        id: 'all',
        label: 'All',
        icon: <DocumentTextIcon className="h-4 w-4" />,
        count: counts.all,
        emptyTitle: 'Nothing here yet',
        emptyHint:
          selectionType === 'equipment'
            ? 'Add an instance, maintenance job, or child equipment for this slot.'
            : 'Add the first child under this node.',
      },
    ];
    if (counts.equipment > 0 || selectionType === 'subcomponent' || selectionType === 'component') {
      tabs.push({
        id: 'equipment',
        label: 'Equipment',
        icon: <CogIcon className="h-4 w-4" />,
        count: counts.equipment,
        emptyTitle: 'No equipment',
        emptyHint: 'No equipment under this node.',
      });
    }
    if (counts.instance > 0 || selectionType === 'equipment') {
      tabs.push({
        id: 'instance',
        label: 'Instances',
        icon: <CogIcon className="h-4 w-4" />,
        count: counts.instance,
        emptyTitle: 'No instances',
        emptyHint: 'No equipment instances under this slot.',
      });
    }
    if (counts.job > 0 || selectionType === 'equipment') {
      tabs.push({
        id: 'job',
        label: 'Jobs',
        icon: <FolderIcon className="h-4 w-4" />,
        count: counts.job,
        emptyTitle: 'No jobs',
        emptyHint: 'No maintenance jobs under this equipment.',
      });
    }
    if (counts.other > 0) {
      tabs.push({
        id: 'other',
        label: 'Other',
        icon: <FolderIcon className="h-4 w-4" />,
        count: counts.other,
        emptyTitle: 'No other children',
        emptyHint: 'No other child records under this node.',
      });
    }
    return tabs;
  }, [counts, selectionType]);

  useEffect(() => {
    if (!viewTabs.some((t) => t.id === tab)) setTab('all');
  }, [viewTabs, tab]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return childRows.filter((row) => {
      if (tab !== 'all' && tabForType(row.type) !== tab) return false;
      if (!q) return true;
      const hay = [
        row.name,
        row.code,
        row.fields?.title,
        row.fields?.serialNumber,
        row.fields?.jobType,
        row.fields?.status,
        row.fields?.criticality,
        row.fields?.department,
        row.fields?.installedLocation,
        row.fields?.nextDue,
        NODE_TYPE_LABELS[row.type],
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [childRows, tab, search]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filteredRows].sort(
      (a, b) =>
        sortValue(a, sortConfig.key).localeCompare(sortValue(b, sortConfig.key), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir,
    );
  }, [filteredRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim());
  const activeTabMeta = viewTabs.find((t) => t.id === tab) ?? viewTabs[0];
  const primaryCreate = createActions.find((a) => a.primary) ?? createActions[0];

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
  };

  return (
    <GeneralList
      className="h-full min-h-0"
      title={title}
      description={description}
      tabs={viewTabs.map(({ id, label, icon, count }) => ({ id, label, icon, count }))}
      activeTab={tab}
      onTabChange={(value) => setTab(value as ChildTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search children…',
        title: 'Searches name, code, serial, job type, status',
      }}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {openSelectedHref && selectionType !== 'vessel' ? (
            <Link
              to={openSelectedHref}
              state={{ from: PMS_HIERARCHY_PATH }}
              onClick={onPersistUi}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              <EyeIcon className="h-4 w-4" />
              Open
            </Link>
          ) : null}
          {editSelectedHref ? (
            <Link
              to={editSelectedHref}
              state={{ from: PMS_HIERARCHY_PATH }}
              onClick={onPersistUi}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </Link>
          ) : null}
          {primaryCreate ? (
            <Link
              to={primaryCreate.href}
              state={{ from: PMS_HIERARCHY_PATH }}
              onClick={onPersistUi}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                colors.primary,
              )}
            >
              <PlusIcon className="h-4 w-4" />
              Add {primaryCreate.label}
            </Link>
          ) : null}
        </div>
      }
      tabTrailing={
        createActions.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1">
            {createActions
              .filter((a) => a !== primaryCreate)
              .map((action) => (
                <Link
                  key={action.type}
                  to={action.href}
                  state={{ from: PMS_HIERARCHY_PATH }}
                  onClick={onPersistUi}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition',
                    colors.textMuted,
                    'hover:text-slate-700 dark:hover:text-slate-200',
                  )}
                  title={action.hint}
                >
                  <PlusIcon className="h-3.5 w-3.5 opacity-70" />
                  {action.label}
                </Link>
              ))}
          </div>
        ) : undefined
      }
      resultSummary={
        !isLoading ? (
          <p className={cn('text-xs', colors.textMuted)}>
            {total === 1 ? '1 child' : `${total} children`}
            {hasFilters ? ' matching filters' : ''}
          </p>
        ) : (
          <span />
        )
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={Boolean(isLoading)}
      loadingLabel="Loading…"
      isEmpty={pageRows.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching children' : activeTabMeta.emptyTitle,
        hint: hasFilters ? 'Try adjusting the search.' : activeTabMeta.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear search
          </button>
        ) : primaryCreate ? (
          <Link
            to={primaryCreate.href}
            state={{ from: PMS_HIERARCHY_PATH }}
            onClick={onPersistUi}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            Add {primaryCreate.label}
          </Link>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn(
                'sticky left-0 z-20 whitespace-nowrap px-3 py-3 font-medium',
                colors.surface,
              )}
              style={{ minWidth: '5.5rem' }}
            />
            <GeneralListSortableTh sortKey="type" className="whitespace-nowrap" style={{ minWidth: '6.5rem' }}>
              Type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap" style={{ minWidth: '14rem' }}>
              Name
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="nextDue" className="whitespace-nowrap" style={{ minWidth: '8.5rem' }}>
              Next due
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="identity" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Serial / job type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="whitespace-nowrap" style={{ minWidth: '8.5rem' }}>
              Status / criticality
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="where" className="whitespace-nowrap" style={{ minWidth: '8rem' }}>
              Location / dept
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="schedule" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Schedule
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const name = row.name || row.fields?.title || '';
            return (
              <tr
                key={`${row.type}:${row.id}`}
                onClick={() => {
                  onPersistUi();
                  navigate(row.detailPath, { state: { from: PMS_HIERARCHY_PATH } });
                }}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td
                  className={cn(
                    'sticky left-0 z-10 whitespace-nowrap px-3 py-3.5',
                    colors.surface,
                    'group-hover:bg-slate-50 dark:group-hover:bg-slate-800',
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={onPersistUi}
                      to={row.detailPath}
                      state={{ from: PMS_HIERARCHY_PATH }}
                      title="View"
                      aria-label="View"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    {row.editPath ? (
                      <Link
                        onClick={onPersistUi}
                        to={row.editPath}
                        state={{ from: PMS_HIERARCHY_PATH }}
                        title="Edit"
                        aria-label="Edit"
                        className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    ) : null}
                    {canDeleteType(row.type) ? (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row);
                        }}
                        className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <span
                    className={cn(
                      'inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium',
                      TYPE_BADGE[row.type],
                    )}
                  >
                    {NODE_TYPE_LABELS[row.type]}
                  </span>
                </td>
                <td className="max-w-[18rem] px-3 py-3.5">
                  <p className={cn('truncate font-medium', colors.text)} title={name}>
                    {name}
                  </p>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  {row.type === 'job' ? (
                    <DueCell nextDue={row.fields?.nextDue} status={row.fields?.dueStatus} />
                  ) : (
                    <span className={colors.textMuted}></span>
                  )}
                </td>
                <td
                  className={cn('max-w-[12rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={identityValue(row)}
                >
                  {identityValue(row)}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  {Boolean(statusValue(row)) ? (
                    <Badge variant="default" className="whitespace-nowrap">
                      {statusValue(row)}
                    </Badge>
                  ) : (
                    <span className={colors.textMuted}></span>
                  )}
                </td>
                <td
                  className={cn('max-w-[10rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={whereValue(row)}
                >
                  {whereValue(row)}
                </td>
                <td
                  className={cn('max-w-[12rem] truncate px-3 py-3.5', colors.textMuted)}
                  title={scheduleValue(row)}
                >
                  {scheduleValue(row)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export { CHILD_TYPE_LABELS };
